import {
  AcceptPendingInput,
  AddGameEventInput,
  AddTxInput,
  ApiError,
  ApiErrorCode,
  AuthUser,
  BootstrapData,
  CreateCategoryInput,
  CreateGoalInput,
  CreateInviteInput,
  CreatedInvite,
  Invite,
  PendingImport,
  PushEventsSettings,
  PutReserveInput,
  SavePushSubscriptionInput,
  SettingKey,
  SweepReserveInput,
  UpdateCategoryInput,
  UpdateGoalInput,
  UpdateTxInput,
} from './types';

/**
 * Все запросы — на тот же origin (Pages Functions). Cloudflare Access-cookie
 * прикладывается автоматически. Никакого токена.
 */
async function send(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: object,
  extraHeaders?: Record<string, string>,
): Promise<Response> {
  const headers: Record<string, string> = { ...extraHeaders };
  if (body) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: Object.keys(headers).length > 0 ? headers : undefined,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
      // manual: редирект Access на кросс-доменный логин при 'follow' падал бы
      // CORS-ошибкой и маскировался под 'network' («офлайн» при живой сети)
      redirect: 'manual',
    });
  } catch (e) {
    throw new ApiError('network', e instanceof Error ? e.message : 'network error');
  }

  if (res.type === 'opaqueredirect') {
    // сервер (Cloudflare Access) редиректит на страницу логина — сессии нет
    throw new ApiError('unauthorized', 'session expired (redirect to login)');
  }
  return res;
}

/** Разбор тела: 204 → undefined, не-JSON и не-2xx → типизированная ApiError. */
async function parse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;

  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch {
    // Протухший Access-cookie: fetch с redirect:'follow' утыкается в
    // HTML-страницу логина (redirected=true) либо в голый 401/403.
    if (res.redirected || res.status === 401 || res.status === 403) {
      throw new ApiError('unauthorized', `session expired (HTTP ${res.status})`);
    }
    throw new ApiError('internal', `non-JSON response (HTTP ${res.status})`);
  }

  if (!res.ok) {
    const data = parsed as { error?: string; message?: string };
    throw new ApiError(
      (data.error as ApiErrorCode) ?? 'internal',
      data.message ?? `HTTP ${res.status}`,
    );
  }
  return parsed as T;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: object,
): Promise<T> {
  return parse<T>(await send(method, path, body));
}

/**
 * Результат условного bootstrap: сервер либо отдал свежее состояние, либо
 * ответил 304 «ничего не менялось» — и тогда тела нет вовсе.
 */
export type BootstrapResult =
  | { notModified: true }
  | { notModified: false; data: BootstrapData };

/**
 * Последний ETag ответа bootstrap. Модульная переменная, а не localStorage:
 * переживать перезагрузку не нужно и вредно — после неё в памяти нет `data`,
 * и 304 оставил бы приложение без состояния.
 */
let bootstrapEtag: string | null = null;

/** Сброс условия: смена сессии (login/logout) и тесты. */
export function resetBootstrapEtag(): void {
  bootstrapEtag = null;
}

/**
 * GET /api/bootstrap с условным запросом.
 *
 * Ради этого весь механизм и заведён (план 3b): при совпадении ETag сервер не
 * читает ни операций, ни агрегатов и отвечает 304 без тела — ~10 строк вместо
 * полной выборки. Тело 304 пустое, парсить его нельзя.
 */
async function bootstrap(): Promise<BootstrapResult> {
  const res = await send(
    'GET',
    '/api/bootstrap',
    undefined,
    bootstrapEtag ? { 'If-None-Match': bootstrapEtag } : undefined,
  );

  if (res.status === 304) return { notModified: true };

  const data = await parse<BootstrapData>(res);
  // ETag запоминаем только после успешного разбора: сохранить метку ответа,
  // который не доехал до состояния, значило бы получать на неё 304 и остаться
  // без данных до следующей записи в household.
  bootstrapEtag = res.headers.get('ETag');
  return { notModified: false, data };
}

export const api = {
  // ---- family auth: сессионная cookie ставится/чистится сервером ----
  // смена сессии обнуляет ETag: следующий bootstrap обязан быть безусловным
  login: async (email: string, password: string) => {
    resetBootstrapEtag();
    return request<AuthUser>('POST', '/api/auth/login', { email, password });
  },
  // invite — токен из ссылки-приглашения; без него регистрация возможна только
  // для email из семейного allowlist (см. таблицу решений в API.md)
  registerUser: async (email: string, password: string, name?: string, invite?: string) => {
    resetBootstrapEtag();
    return request<AuthUser>('POST', '/api/auth/register', {
      email,
      password,
      ...(name ? { name } : {}),
      ...(invite ? { invite } : {}),
    });
  },
  logout: async () => {
    resetBootstrapEtag();
    return request<void>('POST', '/api/auth/logout');
  },
  me: () => request<AuthUser>('GET', '/api/auth/me'),

  bootstrap,
  addTx: (input: AddTxInput) => request<{ id: string }>('POST', '/api/operations', input),
  updateTx: ({ id, ...patch }: UpdateTxInput) =>
    request<{ id: string }>('PATCH', `/api/operations/${encodeURIComponent(id)}`, patch),
  deleteTx: (id: string) =>
    request<void>('DELETE', `/api/operations/${encodeURIComponent(id)}`),
  updateGoal: ({ name, ...patch }: UpdateGoalInput) =>
    request<{ name: string }>('PATCH', `/api/goals/${encodeURIComponent(name)}`, patch),
  createGoal: (input: CreateGoalInput) =>
    request<{ name: string }>('POST', '/api/goals', input),
  deleteGoal: (name: string) =>
    request<void>('DELETE', `/api/goals/${encodeURIComponent(name)}`),
  getGoalImage: (name: string) =>
    request<{ image: string | null }>('GET', `/api/goals/${encodeURIComponent(name)}/image`),
  setGoalImage: (name: string, image: string | null) =>
    request<{ name: string } | void>(
      'PUT',
      `/api/goals/${encodeURIComponent(name)}/image`,
      { image },
    ),
  updateCategory: ({ name, newName, ...patch }: UpdateCategoryInput) =>
    request<{ name: string }>('PATCH', `/api/categories/${encodeURIComponent(name)}`, {
      ...patch,
      ...(newName !== undefined && newName !== name ? { name: newName } : {}),
    }),
  createCategory: (input: CreateCategoryInput) =>
    request<{ name: string }>('POST', '/api/categories', input),
  deleteCategory: (name: string) =>
    request<void>('DELETE', `/api/categories/${encodeURIComponent(name)}`),
  setSetting: (key: SettingKey, value: string | number | null) =>
    request<{ key: string; value: string | null }>(
      'PUT',
      `/api/settings/${encodeURIComponent(key)}`,
      { value },
    ),

  addGameEvent: (input: AddGameEventInput) =>
    request<{ id: string }>('POST', '/api/game-events', input),

  putReserve: (input: PutReserveInput) =>
    request<{ month: string; category: string }>('PUT', '/api/reserves', input),
  sweepReserve: (input: SweepReserveInput) =>
    request<{ id: string; swept: boolean }>('POST', '/api/reserves/sweep', input),

  listPending: () => request<{ items: PendingImport[] }>('GET', '/api/pending'),
  acceptPending: (id: string, input: AcceptPendingInput) =>
    request<{ operation_id: string }>('POST', `/api/pending/${encodeURIComponent(id)}`, input),
  dismissPending: (id: string) =>
    request<void>('DELETE', `/api/pending/${encodeURIComponent(id)}`),

  // ---- приглашения: household берётся сервером из сессии ----
  listInvites: () => request<{ items: Invite[] }>('GET', '/api/invites'),
  createInvite: (input: CreateInviteInput) =>
    request<CreatedInvite>('POST', '/api/invites', input),
  revokeInvite: (idPrefix: string) =>
    request<void>('DELETE', `/api/invites/${encodeURIComponent(idPrefix)}`),

  // ---- токен канала импорта: сам токен сервер отдаёт только при выпуске ----
  getImportTokenState: () => request<{ configured: boolean }>('GET', '/api/settings/import-token'),
  issueImportToken: () => request<{ token: string }>('POST', '/api/settings/import-token'),

  getPushInfo: (endpoint?: string) =>
    request<{ vapid_public_key: string | null; events: PushEventsSettings | null }>(
      'GET',
      endpoint
        ? `/api/push/subscriptions?endpoint=${encodeURIComponent(endpoint)}`
        : '/api/push/subscriptions',
    ),
  savePushSubscription: (input: SavePushSubscriptionInput) =>
    request<{ endpoint: string }>('POST', '/api/push/subscriptions', input),
  updatePushSubscription: (input: { endpoint: string; events?: PushEventsSettings; label?: string }) =>
    request<{ endpoint: string }>('PATCH', '/api/push/subscriptions', input),
  deletePushSubscription: (endpoint: string) =>
    request<void>('DELETE', '/api/push/subscriptions', { endpoint }),
};
